/**
 * WelcomeMessage — 첫 화면 (메시지 없을 때)
 *
 * 에이전트 소개 + starters 칩 목록 표시
 * agent.starters 기반 starter chips
 * persona prop이 있을 경우 PersonaWelcome을 우선 표시합니다 (Wave 4B)
 */

import { Bot, Sparkles, Clock, MessageSquare } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { AgentDefinition } from "@modootoday/core-agent/types";
import type { PersonaDefinition } from "@modootoday/core-agent/persona";
import { PersonaWelcome } from "@/agent/persona/welcome-customizer";
import { useAgentSessions } from "@/agent/data/sessions";

export interface WelcomeMessageProps {
  agent: AgentDefinition | null | undefined;
  onStarterClick: (starter: string) => void;
  isLoading?: boolean;
  /** Wave 4B: 현재 페르소나 (있으면 페르소나별 환영 메시지 + 스타터 표시) */
  persona?: PersonaDefinition | null;
  /** 최근 세션 카드 클릭 시 호출 */
  onSelectSession?: (sessionId: string) => void;
  className?: string;
}

/* ──── 최근 대화 카드 (최대 3개, 모든 agent 통합) ──── */

function RecentSessionsCards({
  onSelectSession,
}: {
  onSelectSession: (sessionId: string) => void;
}) {
  // agentId 필터 없이 모든 세션 로드 — 사용자가 최근 대화를 어떤 agent든 이어갈 수 있도록
  const { data: sessions = [], isLoading } = useAgentSessions();
  const recent = sessions.filter((s) => s.status !== "deleted").slice(0, 3);

  if (isLoading || recent.length === 0) return null;

  return (
    <div className="mt-6 w-full max-w-md space-y-2">
      <div className="flex items-center justify-center gap-1.5 text-[11px] font-medium text-text-tertiary">
        <Clock className="size-3" />
        <span>이전 대화 이어가기</span>
      </div>
      <div className="space-y-1.5">
        {recent.map((session) => (
          <button
            key={session.id}
            type="button"
            onClick={() => onSelectSession(session.id)}
            className="group flex w-full items-start gap-2.5 rounded-xl border border-stroke-subtle bg-surface-raised px-4 py-2.5 text-left transition-colors hover:border-accent/30 hover:bg-surface-secondary"
          >
            <MessageSquare className="mt-0.5 size-3.5 shrink-0 text-text-tertiary group-hover:text-text-brand" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-text-primary">
                {session.title}
              </p>
              <p className="mt-0.5 text-[10px] text-text-tertiary">
                {formatRelative(session.lastMessageAt)} · {session.messageCount}
                개 메시지
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function formatRelative(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffHour = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  if (diffHour < 24) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;
  return date.toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
}

export function WelcomeMessage({
  agent,
  onStarterClick,
  isLoading = false,
  persona,
  onSelectSession,
  className,
}: WelcomeMessageProps) {
  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-center justify-center px-6 py-12 text-center",
        className,
      )}
    >
      {/* 아이콘 */}
      <div className="mb-5 flex size-16 items-center justify-center rounded-2xl bg-accent/10">
        <Bot className="size-8 text-text-brand" />
      </div>

      {/* 제목 */}
      <h2 className="text-[18px] font-bold text-text-primary">
        {isLoading ? "에이전트 불러오는 중..." : (agent?.name ?? "AI 에이전트")}
      </h2>

      {/* 설명 */}
      {agent?.description && (
        <p className="mt-2 max-w-sm text-[13px] leading-relaxed text-text-tertiary">
          {agent.description}
        </p>
      )}

      {/* 페르소나 환영 메시지 + 스타터 (persona가 있으면 우선 표시) */}
      {persona || isLoading ? (
        <div className="mt-8 w-full max-w-md">
          <PersonaWelcome
            persona={persona}
            onStarterClick={onStarterClick}
            isLoading={isLoading}
          />
        </div>
      ) : (
        /* 페르소나 없을 때 — 에이전트 기본 starters */
        <>
          {agent && agent.starters.length > 0 && (
            <div className="mt-8 w-full max-w-md space-y-3">
              <div className="flex items-center justify-center gap-1.5 text-[11px] font-medium text-text-tertiary">
                <Sparkles className="size-3" />
                <span>시작 질문</span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {agent.starters.map((starter, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onStarterClick(starter)}
                    className="rounded-xl border border-stroke-subtle bg-surface-raised px-4 py-3 text-left text-[12px] font-medium text-text-primary hover:bg-surface-secondary hover:border-accent/30 transition-colors"
                  >
                    {starter}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!agent && !isLoading && (
            <p className="mt-4 text-[12px] text-text-tertiary">
              위에서 에이전트를 선택하세요
            </p>
          )}
        </>
      )}

      {/* 최근 대화 카드 — 모든 agent의 최근 세션 3개 */}
      {onSelectSession && (
        <RecentSessionsCards onSelectSession={onSelectSession} />
      )}
    </div>
  );
}
